import React, { useState, useCallback, useEffect } from 'react';

export const NotificationCenterContext = React.createContext();

export function NotificationCenterProvider({ children }) {
  const [notifications, setNotifications] = useState([]); // historique (max 15)
  const [ephemeral, setEphemeral] = useState([]); // popups éphémères
  const [notifWs, setNotifWs] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [username, setUsername] = useState(() => {
    const userStr = window.localStorage.getItem('user');
    if (!userStr) return '';
    try {
      return JSON.parse(userStr).username || '';
    } catch {
      return '';
    }
  });

  // URL WebSocket pour notifications
  function getNotifWsUrl() {
    let baseUrl = process.env.REACT_APP_API_URL || '';
    let wsBase = baseUrl.replace(/^http(s?):\/\//, window.location.protocol === 'https:' ? 'wss://' : 'ws://');
    if (wsBase.endsWith('/')) wsBase = wsBase.slice(0, -1);
    // Ajoute le token JWT si disponible
    let token = '';
    try {
      token = window.localStorage.getItem('token') || '';
    } catch {}
    return wsBase + '/ws/notifications' + (token ? `?token=${encodeURIComponent(token)}` : '');
  }

  // Méthode pour afficher une notification
  const notify = useCallback((notif) => {
    const id = Date.now() + Math.random();
    setNotifications((prev) => {
      const next = [...prev, { ...notif, id }];
      return next.length > 15 ? next.slice(next.length - 15) : next;
    });
    setEphemeral((prev) => [...prev, { ...notif, id }]);
    setTimeout(() => {
      setEphemeral((prev) => prev.filter(n => n.id !== id));
    }, 4500);
  }, []);

  // --- WebSocket persistant pour notifications ---
  useEffect(() => {
    if (!username) return;
    let ws;
    let reconnectTimeout;
    let closedByApp = false;
    function connectWs() {
      console.log('[NotificationCenter] Ouverture WebSocket notifications avec username =', username);
      ws = new window.WebSocket(getNotifWsUrl());
      setNotifWs(ws);
      ws.onopen = () => {
        console.log('[NotificationCenter] WebSocket ouvert, envoi identification:', { user: username });
        setWsConnected(true);
        ws.send(JSON.stringify({ user: username }));
      };
      ws.onmessage = (event) => {
        console.log('[NotificationCenter] Notification reçue via WebSocket:', event.data);
        try {
          const notif = JSON.parse(event.data);
          notify(notif);
        } catch (e) {
          console.error('[NotificationCenter] Erreur parsing notification:', e);
        }
      };
      ws.onclose = () => {
        console.log('[NotificationCenter] WebSocket fermé');
        setWsConnected(false);
        setNotifWs(null);
        if (!closedByApp) {
          reconnectTimeout = setTimeout(connectWs, 2000);
        }
      };
      ws.onerror = (err) => {
        console.error('[NotificationCenter] WebSocket error', err);
        ws.close();
      };
    }
    connectWs();
    return () => {
      closedByApp = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) ws.close();
    };
  }, [username, notify]);

  // Permet de changer d'utilisateur dynamiquement si besoin
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'user') {
        try {
          const u = JSON.parse(e.newValue);
          setUsername(u.username || '');
        } catch {
          setUsername('');
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    console.log('[NotificationCenter] username utilisé pour notifications:', username);
  }, [username]);

  // Retirer une notification
  const remove = (id) => {
    setNotifications((prev) => prev.filter(n => n.id !== id));
    setEphemeral((prev) => prev.filter(n => n.id !== id));
  };

  // Expose le endpoint global JS
  useEffect(() => {
    window.dispatchDesktopNotification = notify;
    return () => { window.dispatchDesktopNotification = undefined; };
  }, [notify]);

  // Gestion du popover historique
  const [showHistory, setShowHistory] = useState(false);
  const [unread, setUnread] = useState(false);
  const bellRef = React.useRef();

  // Marque comme lu quand on ouvre l'historique
  useEffect(() => { if (showHistory) setUnread(false); }, [showHistory]);

  // Allume la cloche à chaque nouvelle notif
  useEffect(() => {
    if (notifications.length > 0) setUnread(true);
  }, [notifications]);

  return (
    <NotificationCenterContext.Provider value={{ notify }}>
      {children}
      {/* Icône de cloche persistante */}
      <div style={{ position: 'fixed', right: 30, bottom: 70, zIndex: 10001 }}>
        <button
          ref={bellRef}
          onClick={() => setShowHistory(v => !v)}
          style={{
            background: 'white',
            border: 'none',
            borderRadius: '50%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            width: 48,
            height: 48,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
          title="Centre de notifications"
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill={unread ? '#1976d2' : 'none'} stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {unread && (
            <span style={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 10,
              height: 10,
              background: '#e53935',
              borderRadius: '50%',
              border: '2px solid white',
            }} />
          )}
        </button>
        {/* Popover historique */}
        {showHistory && (
          <div style={{
            position: 'absolute',
            right: 0,
            bottom: 60,
            minWidth: 280,
            maxHeight: 380,
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 6px 32px rgba(0,0,0,0.18)',
            padding: 0,
            overflowY: 'auto',
            zIndex: 10002,
          }}>
            <div style={{padding: '16px 18px', fontWeight: 700, borderBottom: '1px solid #eee'}}>Notifications</div>
            {notifications.length === 0 ? (
              <div style={{ color: '#888', padding: 22, textAlign: 'center' }}>Aucune notification</div>
            ) : notifications.slice().reverse().map(n => (
              <div
                key={n.id}
                style={{
                  padding: '14px 18px',
                  borderBottom: '1px solid #f0f0f0',
                  cursor: 'pointer',
                  background: '#fff',
                  transition: 'background 0.2s',
                }}
                onClick={() => remove(n.id)}
              >
                <strong style={{color:'#1976d2'}}>{n.title || 'Notification'}</strong>
                <div style={{ fontSize: 14, marginTop: 4 }}>{n.message}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Popups notifications éphémères (comme avant) */}
      <div style={{
        position: 'fixed',
        right: 20,
        bottom: 120, // au-dessus de la cloche
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
      }}>
        {ephemeral.slice(-3).map(n => (
          <div
            key={n.id}
            style={{
              background: '#fff',
              color: '#222',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              borderRadius: 8,
              padding: '14px 24px',
              minWidth: 220,
              marginBottom: 4,
              fontSize: 16,
              cursor: 'pointer',
              borderLeft: '4px solid #1976d2',
              transition: 'opacity 0.3s',
            }}
            onClick={() => remove(n.id)}
          >
            <strong>{n.title || 'Notification'}</strong>
            <div style={{ fontSize: 14, marginTop: 6 }}>{n.message}</div>
          </div>
        ))}
      </div>
    </NotificationCenterContext.Provider>
  );
}
